// FIG Config extraction: the malware configs the node's extractors pulled from the sample and its memory dumps at
// teardown (FLOSS built in, plus the operator's shell plugins: capa, configextractor-py, …). Two journal cells:
//   s8: each extractor's run (ok · empty · timeout · error · disabled, with its time), then one block per finding:
//       family (+ version) · extractor · confidence, over a sheet of C2, URLs, mutexes, paths, keys, decoded strings,
//       campaign, botnet, ATT&CK ids and rule references, each list copyable
//   s4 side: the merge under the lens: families, C2, URLs, mutexes, keys, ATT&CK, the highest confidence, extractors
// Data: config-report.json (configextract.Report) once it loads, else the config.extracted events (derive/config.ts).
//   meta(ctx) → { id: "config", label: "Config", count: findings, empty: no config-report artifact and no event }
import { Fragment, type ReactNode } from "react";
import { CopyText, Empty, FigHead, Kv, MicroTag, figTitleId, type KvRow, type SheetRow, Sheet } from "@/components/ui";
import { useConfigReport } from "@/hooks";
import { count, dur, plural } from "@/lib/format";
import type { FigMeta, FigProps, ReportCtx } from "@/lib/derive/types";
import type { ConfigExtractorRun, ConfigFinding, ConfigKey } from "@/types";
import { techniqueHref } from "../derive/attack";
import { configOf, listOf } from "../derive/config";

export function meta(ctx: ReportCtx): FigMeta {
  const events = ctx.stream.snap.events.filter((e) => e.kind === "config.extracted").length;
  const artifact = ctx.artifacts.some((a) => a.kind === "config-report");
  return { id: "config", label: "Config", count: events || undefined, empty: !artifact && events === 0 };
}

const RUN_WORD: Record<ConfigExtractorRun["status"], string> = { ok: "ok", empty: "empty", timeout: "timeout", error: "error", disabled: "disabled" };

function Runs({ runs }: { runs: readonly ConfigExtractorRun[] }) {
  if (!runs.length) return null;
  return (
    <p className="meta flex flex-wrap gap-x-6 gap-y-1">
      {runs.map((r) => {
        const bad = r.status === "timeout" || r.status === "error";
        return (
          <span key={r.name} className="whitespace-nowrap" title={r.error || undefined}>
            <span className="text-ink" translate="no">
              {r.name}
            </span>{" "}
            <span className={bad ? "text-ink" : r.status === "ok" ? "text-ink-soft" : "text-ink-faint"}>
              {bad && <span aria-hidden="true">× </span>}
              {RUN_WORD[r.status] ?? r.status}
            </span>
            {r.status !== "disabled" && Number(r.duration_ms) > 0 && <span className="text-ink-faint"> · {dur(r.duration_ms)}</span>}
            {r.finding_count > 0 && (
              <span className="text-ink-faint">
                {" "}
                · {count(r.finding_count)} {plural(r.finding_count, "finding")}
              </span>
            )}
          </span>
        );
      })}
    </p>
  );
}

/** A list value: one mono line each, the whole list copyable from the row. */
function lines(k: string, values: readonly string[], label: string): SheetRow | null {
  if (!values.length) return null;
  return {
    k,
    v: (
      <span className="grid gap-0.5">
        {values.map((v) => (
          <span key={v} className="break-all" translate="no">
            {v}
          </span>
        ))}
      </span>
    ),
    copy: { value: values.join("\n"), label },
  };
}

function keyText(key: ConfigKey): string {
  const hex = (key.hex ?? "").replace(/\s+/g, "");
  const size = hex.length >= 2 ? `${hex.length / 2} B` : "";
  return [key.algorithm || "key", size, hex ? (hex.length > 40 ? `${hex.slice(0, 24)}…${hex.slice(-8)}` : hex) : key.ascii ? `"${key.ascii}"` : "", key.iv ? `iv ${key.iv}` : ""]
    .filter(Boolean)
    .join(" · ");
}

function FindingBlock({ f }: { f: ConfigFinding }) {
  const keys = f.keys ?? [];
  const strings = listOf(f.strings);
  const caps = listOf(f.capabilities).map((c) => c.toUpperCase());
  const rules = listOf(f.rule_refs);
  const rows: (SheetRow | null)[] = [
    lines("c2", listOf(f.c2), "C2"),
    lines("urls", listOf(f.urls), "URLs"),
    lines("mutex", listOf(f.mutex), "mutexes"),
    lines("paths", listOf(f.paths), "paths"),
    keys.length
      ? {
          k: keys.length === 1 ? "key" : "keys",
          v: (
            <span className="grid gap-0.5">
              {keys.map((key, i) => (
                <span key={i} className="break-all" translate="no">
                  {keyText(key)}
                </span>
              ))}
            </span>
          ),
          copy: { value: keys.map((key) => key.hex || key.ascii || "").filter(Boolean).join("\n"), label: "keys" },
        }
      : null,
    strings.length
      ? {
          k: "strings",
          v: (
            <span className="inline-flex flex-wrap items-baseline gap-x-3">
              {count(strings.length)} decoded
              <CopyText value={strings.join("\n")} label="decoded strings">
                copy all
              </CopyText>
            </span>
          ),
        }
      : null,
    f.campaign ? { k: "campaign", v: <span translate="no">{f.campaign}</span>, copy: { value: f.campaign, label: "campaign" } } : null,
    f.botnet ? { k: "botnet", v: <span translate="no">{f.botnet}</span>, copy: { value: f.botnet, label: "botnet" } } : null,
    caps.length
      ? {
          k: "att&ck",
          v: (
            <span className="inline-flex flex-wrap gap-x-3">
              {caps.map((c) => (
                <a key={c} className="tech-id" href={techniqueHref(c)} target="_blank" rel="noopener noreferrer" translate="no">
                  {c}
                </a>
              ))}
            </span>
          ),
        }
      : null,
    rules.length
      ? {
          k: "rules",
          v: (
            <span className="inline-flex flex-wrap gap-1.5">
              {rules.map((r) => (
                <MicroTag key={r}>{r}</MicroTag>
              ))}
            </span>
          ),
        }
      : null,
  ];
  const shown = rows.filter((r): r is SheetRow => r !== null);
  // The parser's full per-field dump (NanoCore: BuildTime/Version/
  // *Timeout/*ConnectionHost/…; Remcos: the whole settings table).
  // Kept out of the main Sheet to avoid drowning the IOCs, available
  // under a <details> and as pretty JSON a click away.
  const rawEntries = f.raw
    ? Object.entries(f.raw).map(([k, v]) => [k, typeof v === "object" ? JSON.stringify(v) : String(v)] as const)
    : [];
  const prettyJson = JSON.stringify(
    {
      extractor: f.extractor,
      family: f.family,
      version: f.version,
      confidence: f.confidence,
      c2: f.c2,
      urls: f.urls,
      mutex: f.mutex,
      keys: f.keys,
      strings: f.strings,
      paths: f.paths,
      capabilities: f.capabilities,
      rule_refs: f.rule_refs,
      raw: f.raw,
    },
    null,
    2,
  );
  return (
    <article className="border-t border-rule-row py-4 first:border-t-0 first:pt-0">
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-mono text-[14px] font-semibold text-ink" translate="no">
          {f.family || <span className="font-normal text-ink-faint">no family</span>}
        </span>
        {f.version && (
          <span className="meta" translate="no">
            v{f.version.replace(/^v/i, "")}
          </span>
        )}
        <span className="meta" translate="no">
          {f.extractor}
        </span>
        {f.confidence ? (
          <span className="meta ml-auto">
            confidence <b className="font-medium text-ink">{f.confidence}</b>
          </span>
        ) : null}
      </header>
      {shown.length ? <Sheet rows={shown} className="mt-3 min-[481px]:grid-cols-[96px_minmax(0,1fr)]" /> : <p className="meta mt-2">no values</p>}
      {rawEntries.length > 0 && (
        <details className="mt-3 border border-rule-row">
          <summary className="cursor-pointer px-3 py-2 text-ui text-ink hover:bg-paper">
            Full extractor dump <span className="faint">· {rawEntries.length} fields</span>
          </summary>
          <div className="tscroll max-h-[40vh] overflow-auto">
            <table className="t t--lg w-full">
              <tbody>
                {rawEntries.map(([k, v]) => (
                  <tr key={k}>
                    <td className="w-[200px] py-1.5 pr-4 pl-3 align-top font-mono text-[11.5px] text-ink-soft" translate="no">
                      {k}
                    </td>
                    <td className="py-1.5 pr-3 align-top font-mono text-[11.5px] break-all text-ink" translate="no">
                      {v}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
      <details className="mt-2 border border-rule-row">
        <summary className="flex cursor-pointer items-center justify-between px-3 py-2 text-ui text-ink hover:bg-paper">
          <span>Raw JSON</span>
          <CopyText value={prettyJson} label="finding JSON">
            copy
          </CopyText>
        </summary>
        <pre className="tscroll max-h-[52vh] overflow-auto bg-paper p-3 font-mono text-[11.5px] leading-[1.45] text-ink">{prettyJson}</pre>
      </details>
    </article>
  );
}

function more(values: readonly string[], n = 1): ReactNode {
  if (!values.length) return null;
  return (
    <>
      <span translate="no">{values.slice(0, n).join(", ")}</span>
      {values.length > n && <small> +{values.length - n}</small>}
    </>
  );
}

export default function ConfigFig({ ctx, no }: FigProps) {
  const q = useConfigReport(ctx.job.id, ctx.artifacts);
  const view = configOf(q.data, ctx.stream.snap.events);
  const n = view.findings.length;
  const runs = view.extractors;
  const ok = runs.filter((r) => r.status === "ok" || r.status === "empty").length;
  const failed = runs.filter((r) => r.status === "timeout" || r.status === "error").length;
  const extractorNames = runs.length ? runs.filter((r) => r.status !== "disabled").map((r) => r.name) : [...new Set(view.findings.map((f) => f.extractor))];

  const rows: KvRow[] = [
    ["Families", more(view.families, 2)],
    ["C2", view.c2.length ? <>{count(view.c2.length)}<small> · {view.c2[0]}</small></> : null],
    ["URLs", view.urls.length ? count(view.urls.length) : null],
    ["Mutexes", view.mutex.length ? count(view.mutex.length) : null],
    ["Keys", view.keys ? count(view.keys) : null],
    ["ATT&CK", view.capabilities.length ? count(view.capabilities.length) : null],
    ["Confidence", view.confidence !== null ? <>{view.confidence}<small>/100</small></> : null],
    [
      "Extractors",
      runs.length ? (
        <>
          {count(ok)} ran<small>{failed ? ` · ${count(failed)} failed` : ""} · of {count(runs.filter((r) => r.status !== "disabled").length)}</small>
        </>
      ) : extractorNames.length ? (
        <span translate="no">{extractorNames.join(", ")}</span>
      ) : null,
    ],
  ];

  return (
    <>
      <section className="cell s8 fig" aria-labelledby={figTitleId(no)}>
        <FigHead
          no={no}
          label="Config extraction"
          count={`${count(n)} ${plural(n, "finding")}${extractorNames.length ? ` · ${count(extractorNames.length)} ${plural(extractorNames.length, "extractor")}` : ""}`}
        />
        <div className="fig-body">
          <Runs runs={runs} />
          <div className={runs.length ? "mt-5" : undefined}>
            {n ? (
              view.findings.map((f, i) => (
                <Fragment key={`${f.extractor}:${f.family ?? ""}:${i}`}>
                  <FindingBlock f={f} />
                </Fragment>
              ))
            ) : (
              <Empty inline rule={false}>
                {q.isPending && ctx.artifacts.some((a) => a.kind === "config-report") ? "Loading config-report.json…" : "No config extracted"}
              </Empty>
            )}
          </div>
        </div>
      </section>
      <section className="cell s4 side fig" aria-label="Config extraction details">
        <p className="lens-eyebrow">Under the lens · config</p>
        <Kv rows={rows} className="mt-3" />
        {view.c2.length > 0 && (
          <p className="mt-4">
            <CopyText value={view.c2.join("\n")} label="C2 addresses">
              copy {count(view.c2.length)} C2
            </CopyText>
          </p>
        )}
      </section>
    </>
  );
}
